/** Shared API ↔ UI mappers for Treasury (Caisse) pages */

export function pickField(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== '') return obj[key];
    const lower = key.toLowerCase();
    if (obj[lower] != null && obj[lower] !== '') return obj[lower];
  }
  return undefined;
}

export function parseOperationNotes(notes) {
  if (!notes || typeof notes !== 'string') return {};
  if (!notes.trim().startsWith('{')) return { rawNotes: notes };
  try {
    return JSON.parse(notes);
  } catch {
    return { rawNotes: notes };
  }
}

export function mapJournalOperationFromApi(op) {
  const extra = parseOperationNotes(op.notes);
  const credit =
    op.credit ?? (op.operation_type === 'receipt' ? Number(op.amount) || 0 : 0);
  const debit =
    op.debit ?? (op.operation_type === 'payment' ? Number(op.amount) || 0 : 0);

  return {
    id: op.id,
    reference: op.reference || (op.id != null ? `OPJ-${String(op.id).padStart(3, '0')}` : ''),
    date: op.operation_date || op.date,
    designation: op.description || op.designation || '',
    credit,
    debit,
    amount: op.amount,
    affectation: extra.affectation || op.category || '',
    notes: extra.rawNotes || extra.notes || '',
    caisse: op.caisse_name || op.caisse || '',
    caisse_id: op.caisse_id,
    operationType: op.category || '',
    responsable: extra.responsable || '',
    responsableId: extra.responsable_id || '',
    temoin: extra.temoin || '',
    temoinId: extra.temoin_id || '',
    immatriculation: extra.immatriculation || '',
    quantite: extra.quantite ?? 1,
    unite: extra.unite || 'unité',
    prixUnitaire: extra.prix_unitaire ?? '',
    acheteur: extra.acheteur || op.payee_payer || '',
    paymentMethod: op.payment_method || extra.payment_method || 'cash',
    solde: op.solde,
    bonCommandeId: extra.bon_commande_id,
    bonCommande: extra.bon_commande_number,
    bonCommandeType: extra.bon_commande_type,
    status: op.status || 'pending',
  };
}

export function mapJournalOperationToApi(form, caisseId = 3) {
  const isCredit = Boolean(form.credit && Number(form.credit) > 0);
  const amount = Number(isCredit ? form.credit : form.debit) || 0;
  const responsableName = form.responsable || '';
  const temoinName = form.temoin || '';

  return {
    caisse_id: form.caisse_id || caisseId,
    operation_date: form.date,
    operation_type: isCredit ? 'receipt' : 'payment',
    reference: form.reference || null,
    designation: form.designation,
    category: form.operationType || null,
    amount,
    payee_payer: form.acheteur || null,
    payment_method: form.paymentMethod || 'cash',
    affectation: form.affectation || 'DEFAULT',
    immatriculation: form.immatriculation || null,
    quantite: Number(form.quantite) || 1,
    unite: form.unite || 'unité',
    prix_unitaire: form.prixUnitaire ? Number(form.prixUnitaire) : null,
    acheteur: form.acheteur || null,
    responsable_id: form.responsableId || null,
    responsable: responsableName || null,
    temoin_id: form.temoinId || null,
    temoin: temoinName || null,
    bon_commande_id: form.bonCommandeId || null,
    bon_commande_number: form.bonCommande || null,
    bon_commande_type: form.bonCommandeType || null,
    notes: form.notes || null,
  };
}

export function mapPrincipalOperationFromApi(op) {
  if (op.credit != null || op.debit != null) {
    let affectation = op.affectation;
    if (!affectation) {
      const extra = parseOperationNotes(op.notes);
      affectation = extra.affectation || op.category || '';
    }
    return {
      ...op,
      id: op.id,
      reference: op.reference || (op.id != null ? `OPM-${String(op.id).padStart(3, '0')}` : ''),
      date: op.date || op.operation_date,
      designation: op.designation || op.description || '',
      credit: Number(op.credit) || 0,
      debit: Number(op.debit) || 0,
      affectation,
      caisse: op.caisse || op.caisse_name || '',
      solde: op.solde,
    };
  }
  return mapJournalOperationFromApi(op);
}

export function mapPrincipalOperationToApi(form, caisseId = 1) {
  const isCredit = Boolean(form.credit && Number(form.credit) > 0);
  const amount = Number(isCredit ? form.credit : form.debit) || 0;

  return {
    caisse_id: caisseId,
    operation_date: form.date,
    operation_type: isCredit ? 'receipt' : 'payment',
    reference: form.reference || null,
    designation: form.designation,
    category: form.operationType || null,
    amount,
    affectation: form.affectation || 'DEFAULT',
    notes: form.notes || null,
    payee_payer: form.responsable || null,
    payment_method: form.paymentMethod || 'cash',
  };
}

const VOUCHER_TYPE_TO_UI = {
  bon_de_sortie: 'expense',
  bon_d_entree: 'petty_cash',
  bon_de_versement: 'advance',
  petty_cash: 'petty_cash',
  expense: 'expense',
  advance: 'advance',
  internal: 'petty_cash',
  external: 'expense',
};

const VOUCHER_UI_TO_TYPE = {
  petty_cash: 'bon_d_entree',
  expense: 'bon_de_sortie',
  advance: 'bon_de_versement',
  internal: 'bon_d_entree',
  external: 'bon_de_sortie',
};

const VOUCHER_STATUS_TO_UI = {
  issued: 'pending',
  cashed: 'completed',
  cancelled: 'cancelled',
};

const VOUCHER_UI_TO_STATUS = {
  pending: 'issued',
  completed: 'cashed',
  cancelled: 'cancelled',
};

export function mapVoucherFromApi(bon) {
  return {
    id: String(bon.id),
    invoiceNumber: bon.bon_number,
    date: bon.bon_date,
    clientName: bon.beneficiary || '',
    total: Number(bon.amount) || 0,
    paid: bon.status === 'cashed' ? Number(bon.amount) || 0 : 0,
    status: VOUCHER_STATUS_TO_UI[bon.status] || 'pending',
    type: VOUCHER_TYPE_TO_UI[bon.bon_type] || 'internal',
    designation: bon.reason || '',
    caisse_id: bon.caisse_id,
    bon_type: bon.bon_type,
    apiStatus: bon.status,
    caisse_name: bon.caisse_name,
  };
}

export function mapVoucherToApi(form, { caisseId = 1, invoiceNumber } = {}) {
  const amount =
    parseFloat(form.montantTTC) ||
    parseFloat(form.montantHT) ||
    parseFloat(form.total) ||
    0;

  return {
    caisse_id: caisseId,
    bon_type: VOUCHER_UI_TO_TYPE[form.typeBon] || 'bon_de_sortie',
    bon_number: invoiceNumber || form.invoiceNumber || null,
    bon_date: form.date,
    amount,
    beneficiary: form.beneficiaire || form.clientName || '',
    reason: form.designation || form.notes || '',
    status: VOUCHER_UI_TO_STATUS[form.status] || 'issued',
  };
}

export function mapTransferFromApi(t) {
  return {
    id: t.id,
    date: t.transfer_date,
    reference: t.reference || (t.id != null ? `TRF-${String(t.id).padStart(3, '0')}` : ''),
    source: t.from_caisse_name || '',
    destination: t.to_caisse_name || t.to_bank_name || '',
    montant: Number(t.amount) || 0,
    motif: t.description || '',
    responsable: t.created_by || t.approved_by || '',
    status: t.status || 'pending',
    from_caisse_id: t.from_caisse_id,
    to_caisse_id: t.to_caisse_id,
    to_bank_account_id: t.to_bank_account_id,
    transfer_type: t.transfer_type,
  };
}

export function resolveCaisseId(name, accounts = []) {
  if (!name) return null;
  const match = accounts.find(
    (a) => a.name === name || a.label === name || a.value === name
  );
  return match?.id ?? match?.value ?? null;
}

export function formatBankAccountLabel(account) {
  if (!account) return '';
  return `${account.bank_name || ''} — ${account.account_name || account.account_number || account.name || ''}`.trim();
}

export function resolveBankAccountId(name, bankAccounts = []) {
  if (!name) return null;
  const normalized = String(name).trim();
  const match = bankAccounts.find((b) => {
    const dashLabel = formatBankAccountLabel(b);
    const spaceLabel = `${b.bank_name || ''} ${b.account_name || b.name || ''}`.trim();
    return (
      b.account_name === normalized ||
      b.name === normalized ||
      dashLabel === normalized ||
      spaceLabel === normalized ||
      String(b.id) === normalized
    );
  });
  return match?.id ?? null;
}

const EMBEDDED_REF_PATTERN = /^\[REF:([^\]]+)\]\s?/;

function parseEmbeddedReference(notes, fallback) {
  const match = String(notes || '').match(EMBEDDED_REF_PATTERN);
  return match ? match[1].trim() : fallback;
}

function stripEmbeddedReference(notes) {
  return String(notes || '').replace(EMBEDDED_REF_PATTERN, '').trim();
}

function embedReferenceInNotes(reference, notes) {
  const ref = String(reference || '').trim();
  const body = stripEmbeddedReference(notes);
  if (!ref) return body || null;
  return body ? `[REF:${ref}] ${body}` : `[REF:${ref}]`;
}

export function mapTransferToApi(form, accounts = [], bankAccounts = []) {
  const fromId = resolveCaisseId(form.source, accounts);
  const toCaisseId = resolveCaisseId(form.destination, accounts);
  const toBankId = resolveBankAccountId(form.destination, bankAccounts);
  const toBank = toBankId ? bankAccounts.find((b) => b.id === toBankId) : null;

  const transferType = toBank ? 'caisse_to_bank' : 'caisse_to_caisse';

  return {
    from_caisse_id: fromId || accounts[0]?.id || 1,
    to_caisse_id: transferType === 'caisse_to_caisse' ? toCaisseId : null,
    to_bank_account_id: transferType === 'caisse_to_bank' ? toBank?.id : null,
    transfer_date: form.date,
    amount: parseFloat(form.montant) || 0,
    reference: form.reference || null,
    description: form.motif || null,
    transfer_type: transferType,
  };
}

const CLOSURE_STATUS_TO_UI = { pending: 'pending', approved: 'closed', rejected: 'pending' };
const CLOSURE_STATUS_TO_API = { pending: 'pending', closed: 'approved' };

export function mapClosureFromApi(c) {
  if (!c) return c;
  const fallbackRef = c.id != null ? `CLS-${String(c.id).padStart(3, '0')}` : '';
  return {
    id: c.id,
    reference: parseEmbeddedReference(c.notes, c.reference || fallbackRef),
    date: c.closure_date,
    caissier: c.approved_by || c.caisse_name || '',
    soldeOuverture: Number(c.opening_balance) || 0,
    totalEntrees: Number(c.total_receipts) || 0,
    totalSorties: Number(c.total_payments) || 0,
    soldeFermeture: Number(c.expected_balance) || 0,
    soldeReel: Number(c.actual_balance) || 0,
    ecart: Number(c.difference) || 0,
    notes: stripEmbeddedReference(c.notes),
    status: CLOSURE_STATUS_TO_UI[c.status] || c.status || 'pending',
    caisse_id: c.caisse_id,
    caisse_name: c.caisse_name,
  };
}

export function mapClosureToApi(form, caisseId = 1) {
  const opening = Number(form.soldeOuverture) || 0;
  const receipts = Number(form.totalEntrees) || 0;
  const payments = Number(form.totalSorties) || 0;
  const expected = opening + receipts - payments;
  const actual = Number(form.soldeReel) || 0;
  return {
    caisse_id: form.caisse_id || caisseId,
    closure_date: form.date,
    opening_balance: opening,
    total_receipts: receipts,
    total_payments: payments,
    expected_balance: expected,
    actual_balance: actual,
    notes: embedReferenceInNotes(form.reference, form.notes),
  };
}

export function mapReconciliationFromApi(r) {
  if (!r) return r;
  const fallbackRef = r.id != null ? `RAP-${String(r.id).padStart(3, '0')}` : '';
  return {
    id: r.id,
    reference: parseEmbeddedReference(r.notes, r.reference || fallbackRef),
    date: r.reconciliation_date,
    periode: r.periode || '',
    caisse: r.caisse_name || '',
    caisse_id: r.caisse_id,
    soldeSysteme: Number(r.solde_systeme) || 0,
    soldePhysique: Number(r.solde_physique) || 0,
    ecart: Number(r.ecart) || 0,
    notes: stripEmbeddedReference(r.notes),
    status: r.status || 'pending',
  };
}

export function mapReconciliationToApi(form, caisseId = 1) {
  return {
    caisse_id: form.caisse_id || caisseId,
    reconciliation_date: form.date,
    periode: form.periode,
    solde_systeme: Number(form.soldeSysteme) || 0,
    solde_physique: Number(form.soldePhysique) || 0,
    ecart: Number(form.soldePhysique) - Number(form.soldeSysteme),
    notes: embedReferenceInNotes(form.reference, form.notes),
    status: form.status || 'pending',
  };
}

export function mapMovementHistoryFromApi(row) {
  if (!row) return row;
  const isReceipt = row.operation_type === 'receipt';
  const isPayment = row.operation_type === 'payment';
  return {
    id: row.id,
    date: row.operation_date || row.date || '',
    type: isReceipt ? 'entree' : isPayment ? 'sortie' : 'transfert',
    reference: row.reference || row.bon_number || (row.id != null ? `OP-${row.id}` : ''),
    description: row.description || row.designation || '',
    montant: Number(row.amount) || 0,
    solde: Number(row.solde) || 0,
    caisse: row.caisse_name || row.caisse || '',
    utilisateur: row.created_by || '',
    status: row.status || 'validated',
  };
}

export function normalizeDashboardData(raw) {
  const caisses = raw?.caisses || {};
  const month = raw?.month || {};
  const today = raw?.today || {};
  const pendingTransfers = raw?.pendingTransfers || [];
  const operationsByCategory = raw?.operationsByCategory || [];
  const dailyCashFlow = raw?.dailyCashFlow || [];
  const balanceByCaisse = raw?.balanceByCaisse || [];
  const recentOperations = raw?.recentOperations || [];

  const totalBalance = Number(pickField(caisses, 'totalBalance', 'totalbalance')) || 0;
  const monthlyIncome = Number(pickField(month, 'totalReceipts', 'totalreceipts')) || 0;
  const monthlyExpenses = Number(pickField(month, 'totalPayments', 'totalpayments')) || 0;
  const totalOperations = Number(pickField(month, 'totalOperations', 'totaloperations')) || 0;
  const todayOps = Number(pickField(today, 'totalOperations', 'totaloperations')) || 0;

  const receiptOps = operationsByCategory.filter((c) => c.operation_type === 'receipt');
  const paymentOps = operationsByCategory.filter((c) => c.operation_type === 'payment');
  const incomeCount = receiptOps.reduce((s, c) => s + (Number(c.count) || 0), 0);
  const expenseCount = paymentOps.reduce((s, c) => s + (Number(c.count) || 0), 0);
  const pendingVouchers = pendingTransfers.length;

  const issuedBons = recentOperations.filter((op) => op.bon_number).length;

  return {
    cashBalance: totalBalance,
    monthlyIncome,
    monthlyExpenses,
    netCashFlow: monthlyIncome - monthlyExpenses,
    pendingVouchers,
    activeAlerts: pendingTransfers.filter((t) => t.status === 'pending').length,
    totalOperations,
    todayOps,
    incomeCount,
    expenseCount,
    dailyCashFlow,
    operationsByCategory,
    balanceByCaisse,
    pendingTransfers,
    recentOperations,
    issuedBons,
  };
}
